import {
  Button,
  Alert,
  Combobox,
  MultiSelect,
  FormField,
  TextInput,
  TextArea,
  useToast,
} from "@a2z/ui";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { ThemeFamily } from "@/lib/api/types";
import { THEME_FAMILIES, THEME_NICHES } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useCreateThemeMutation } from "../services/useCreateThemeMutation";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";

const FAMILY_LABEL_KEY: Record<ThemeFamily, string> = {
  starter: keys.themes.families.starter,
};

export function ThemeCreateForm({ locale }: { locale: Locale }) {
  const navigate = useNavigate();
  const t = useTranslations();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [family, setFamily] = useState<ThemeFamily>("starter");
  const [niches, setNiches] = useState<string[]>([]);
  const { mutateAsync: createTheme } = useCreateThemeMutation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const requiredErrors = getRequiredFieldErrors(
      formData,
      ["slug", "nameAr", "nameEn"],
      t(keys.errors.required),
    );
    if (Object.keys(requiredErrors).length > 0) {
      setFieldErrors(requiredErrors);
      setIsSubmitting(false);
      return;
    }

    const sortWeightRaw = String(formData.get("sortWeight") ?? "").trim();
    const sortWeight =
      sortWeightRaw.length > 0 ? Number(sortWeightRaw) : undefined;
    if (sortWeight !== undefined && !Number.isFinite(sortWeight)) {
      setFieldErrors({ sortWeight: t(keys.errors.required) });
      setIsSubmitting(false);
      return;
    }

    const descriptionAr = String(formData.get("descriptionAr") ?? "").trim();
    const descriptionEn = String(formData.get("descriptionEn") ?? "").trim();

    try {
      const theme = await createTheme({
        family,
        slug: String(formData.get("slug") ?? "").trim(),
        nameAr: String(formData.get("nameAr") ?? "").trim(),
        nameEn: String(formData.get("nameEn") ?? "").trim(),
        descriptionAr: descriptionAr.length > 0 ? descriptionAr : undefined,
        descriptionEn: descriptionEn.length > 0 ? descriptionEn : undefined,
        niches: niches.length > 0 ? niches : undefined,
        sortWeight,
      });
      toast.success(t(keys.common.updateSuccess));
      navigate(`/${locale}/admin/themes/${theme.id}`, { replace: true });
    } catch (submitError) {
      if (submitError instanceof ApiJsonClientError) {
        setError(submitError.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className="mx-auto flex w-full max-w-[640px] flex-col gap-4"
      onSubmit={handleSubmit}
    >
      {error ? (
        <Alert message={error} helperText={t(keys.errors.recovery)} />
      ) : null}
      <FormField
        label={t(keys.themes.family)}
        name="family"
        required
        error={fieldErrors.family}
      >
        <Combobox
          options={THEME_FAMILIES.map((value) => ({
            value,
            label: t(FAMILY_LABEL_KEY[value]),
          }))}
          value={family}
          onChange={(value) => setFamily((value as ThemeFamily) ?? "starter")}
        />
      </FormField>
      <FormField
        label={t(keys.themes.slug)}
        name="slug"
        required
        error={fieldErrors.slug}
      >
        <TextInput
          name="slug"
          placeholder={t(keys.themes.slugPlaceholder)}
          required
          dir="ltr"
          hasError={Boolean(fieldErrors.slug)}
        />
      </FormField>
      <div className="grid gap-4 lg:grid-cols-2">
        <FormField
          label={t(keys.themes.nameAr)}
          name="nameAr"
          required
          error={fieldErrors.nameAr}
        >
          <TextInput
            name="nameAr"
            dir="rtl"
            required
            hasError={Boolean(fieldErrors.nameAr)}
          />
        </FormField>
        <FormField
          label={t(keys.themes.nameEn)}
          name="nameEn"
          required
          error={fieldErrors.nameEn}
        >
          <TextInput
            name="nameEn"
            dir="ltr"
            required
            hasError={Boolean(fieldErrors.nameEn)}
          />
        </FormField>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <FormField label={t(keys.themes.descriptionAr)} name="descriptionAr">
          <TextArea name="descriptionAr" dir="rtl" rows={3} />
        </FormField>
        <FormField label={t(keys.themes.descriptionEn)} name="descriptionEn">
          <TextArea name="descriptionEn" dir="ltr" rows={3} />
        </FormField>
      </div>
      <FormField label={t(keys.themes.niches)} name="niches">
        <MultiSelect
          options={THEME_NICHES.map((value) => ({
            value,
            label: t(keys.themes.nicheLabels[value]),
          }))}
          value={niches}
          onChange={setNiches}
          placeholder={t(keys.themes.nichesPlaceholder)}
          aria-label={t(keys.themes.niches)}
        />
      </FormField>
      <FormField label={t(keys.themes.sortWeight)} name="sortWeight">
        <TextInput
          name="sortWeight"
          type="number"
          inputMode="numeric"
          dir="ltr"
          placeholder="0"
        />
      </FormField>
      <Button
        type="submit"
        disabled={isSubmitting}
        variant="primary"
        className="h-11 w-full rounded-lg px-4 text-sm font-semibold transition disabled:opacity-60"
      >
        {t(keys.common.create)}
      </Button>
    </form>
  );
}
